import { useState, useMemo } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Z_INDEX } from '@/constants/zIndex';
import { useMacroStore, type MacroSeries } from '@/store/macroStore';
import { trackMacroAction } from '@/services/gtag';
import { SearchIcon, PlusIcon } from '@/components/icons';

interface MacroPickerModalProps {
  onClose: () => void;
  /** Optional: chartRef passed from IndicatorBar (unused here, panes managed in ChartContainer) */
  chartRef?: React.RefObject<unknown>;
}

/** Group series by category */
function groupByCategory(series: MacroSeries[]): Array<{ category: string; items: MacroSeries[] }> {
  const map = new Map<string, MacroSeries[]>();
  for (const s of series) {
    const list = map.get(s.category) ?? [];
    list.push(s);
    map.set(s.category, list);
  }
  return Array.from(map.entries()).map(([category, items]) => ({ category, items }));
}

/** Build parent/child hierarchy within a category */
function buildHierarchy(items: MacroSeries[]) {
  const parents = items.filter((s) => !s.parent_series_id);
  const children = items.filter((s) => s.parent_series_id);
  const childMap = children.reduce<Record<string, MacroSeries[]>>((acc, s) => {
    (acc[s.parent_series_id!] ??= []).push(s);
    return acc;
  }, {});
  return { parents, childMap };
}

export function MacroPickerModal({ onClose }: MacroPickerModalProps) {
  const seriesMeta = useMacroStore((s) => s.seriesMeta);
  const pinnedSeries = useMacroStore((s) => s.pinnedSeries);
  const addSeries = useMacroStore((s) => s.addSeries);
  const removeSeries = useMacroStore((s) => s.removeSeries);
  const metaLoading = useMacroStore((s) => s.metaLoading);

  const [query, setQuery] = useState('');
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  const pinnedIds = useMemo(() => new Set(pinnedSeries.map((s) => s.seriesId)), [pinnedSeries]);

  const filtered = useMemo(() => {
    if (!query.trim()) return seriesMeta;
    const q = query.toLowerCase();
    return seriesMeta.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.series_id.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q),
    );
  }, [seriesMeta, query]);

  const grouped = useMemo(() => groupByCategory(filtered), [filtered]);

  function handleToggle(seriesId: string) {
    if (pinnedIds.has(seriesId)) {
      trackMacroAction('unpin', seriesId);
      removeSeries(seriesId);
    } else {
      trackMacroAction('pin', seriesId);
      addSeries(seriesId);
    }
  }

  function toggleExpand(seriesId: string) {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(seriesId)) next.delete(seriesId);
      else next.add(seriesId);
      return next;
    });
  }

  /** Render a single series row (parent or child) */
  function SeriesRow({ series, indent = false }: { series: MacroSeries; indent?: boolean }) {
    const isPinned = pinnedIds.has(series.series_id);
    return (
      <button
        key={series.series_id}
        onClick={() => handleToggle(series.series_id)}
        className={`flex items-center justify-between gap-3 py-2 rounded-lg text-left transition-colors
          ${isPinned ? 'bg-accent/10 border border-accent/30' : 'hover:bg-panel-hover border border-transparent'}
          ${indent ? 'pl-6 pr-3' : 'px-3'}`}
      >
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium leading-snug truncate ${isPinned ? 'text-accent' : 'text-text'}`}>
            {series.name}
          </p>
          <p className="text-[11px] text-muted mt-0.5">{series.frequency} · {series.series_id}</p>
        </div>
        <span
          className={`shrink-0 flex items-center justify-center w-5 h-5 rounded-full transition-colors
            ${isPinned
              ? 'bg-accent text-white'
              : 'bg-panel-bg border border-border text-muted hover:border-accent/60 hover:text-accent'
            }`}
        >
          {isPinned ? (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <PlusIcon size={10} />
          )}
        </span>
      </button>
    );
  }

  return (
    <Modal size="md" align="top" onClose={onClose} zIndex={Z_INDEX.modalElevated}>
      <Modal.Header title="Add Macro Indicator" onClose={onClose} />
      <Modal.Body className="gap-3 max-h-[70vh]">
        {/* Search */}
        <div className="relative">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search indicators…"
            autoFocus
            className="w-full pl-8 pr-3 py-2 text-sm bg-panel-bg border border-border rounded-lg text-text placeholder:text-muted focus:outline-none focus:border-accent/60 transition-colors"
          />
        </div>

        {/* List */}
        {metaLoading ? (
          <div className="flex items-center justify-center py-10 text-muted text-sm">Loading…</div>
        ) : grouped.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-muted text-sm">No indicators found</div>
        ) : (
          <div className="flex flex-col gap-4 overflow-y-auto">
            {grouped.map(({ category, items }) => {
              // When searching, show flat list (no hierarchy)
              if (query.trim()) {
                return (
                  <div key={category}>
                    <p className="text-[10px] font-semibold text-muted uppercase tracking-wider mb-1.5 px-0.5">
                      {category}
                    </p>
                    <div className="flex flex-col gap-0.5">
                      {items.map((series) => (
                        <SeriesRow key={series.series_id} series={series} />
                      ))}
                    </div>
                  </div>
                );
              }

              // Hierarchy mode
              const { parents, childMap } = buildHierarchy(items);
              const parentIds = new Set(parents.map((p) => p.series_id));
              const orphanChildren = items.filter(
                (s) => s.parent_series_id && !parentIds.has(s.parent_series_id),
              );

              return (
                <div key={category}>
                  <p className="text-[10px] font-semibold text-muted uppercase tracking-wider mb-1.5 px-0.5">
                    {category}
                  </p>
                  <div className="flex flex-col gap-0.5">
                    {parents.map((series) => {
                      const hasChildren = (childMap[series.series_id]?.length ?? 0) > 0;
                      const expanded = expandedParents.has(series.series_id);
                      const isPinned = pinnedIds.has(series.series_id);
                      return (
                        <div key={series.series_id}>
                          {/* Parent row with embedded chevron */}
                          <button
                            onClick={() => handleToggle(series.series_id)}
                            className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left transition-colors
                              ${isPinned ? 'bg-accent/10 border border-accent/30' : 'hover:bg-panel-hover border border-transparent'}`}
                          >
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                              {hasChildren ? (
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(series.series_id);
                                  }}
                                  className="shrink-0 w-5 h-5 flex items-center justify-center text-muted hover:text-text cursor-pointer select-none"
                                  title={expanded ? 'Collapse' : 'Expand'}
                                >
                                  <span className="text-[10px]">{expanded ? '▼' : '▶'}</span>
                                </span>
                              ) : (
                                <span className="shrink-0 w-5" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium leading-snug truncate ${isPinned ? 'text-accent' : 'text-text'}`}>
                                  {series.name}
                                </p>
                                <p className="text-[11px] text-muted mt-0.5">{series.frequency} · {series.series_id}</p>
                              </div>
                            </div>
                            <span
                              className={`shrink-0 flex items-center justify-center w-5 h-5 rounded-full transition-colors
                                ${isPinned
                                  ? 'bg-accent text-white'
                                  : 'bg-panel-bg border border-border text-muted hover:border-accent/60 hover:text-accent'
                                }`}
                            >
                              {isPinned ? (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              ) : (
                                <PlusIcon size={10} />
                              )}
                            </span>
                          </button>
                          {/* Children */}
                          {expanded && childMap[series.series_id]?.map((child) => (
                            <SeriesRow key={child.series_id} series={child} indent />
                          ))}
                        </div>
                      );
                    })}
                    {/* Orphan children (parent not in this category) */}
                    {orphanChildren.map((series) => (
                      <SeriesRow key={series.series_id} series={series} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <p className="text-[11px] text-muted flex-1">
          {pinnedIds.size} indicator{pinnedIds.size !== 1 ? 's' : ''} selected
        </p>
        <button
          onClick={onClose}
          className="px-4 py-1.5 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
        >
          Done
        </button>
      </Modal.Footer>
    </Modal>
  );
}
